// Core data shapes for the frontend validation engine. Everything in engine/
// is pure and worker-safe: no React, redux, Inversify or DOM imports.

import { ValidationRule, WorksheetRelationship } from "../../../templateConfig/validationRuleTypes";

// Cell values are normalised to strings at read time: trimmed cell text, Excel date cells
// as ISO "YYYY-MM-DD", numbers as plain strings, "" = blank.
export type CellValue = string;

export type WorksheetRows = {
  // Template worksheet name (post-association).
  worksheet: string;
  // Header names in file order.
  sourceColumns: string[];
  // rows[i][j] pairs with sourceColumns[j]; fully-blank rows are dropped.
  rows: CellValue[][];
  // Original 1-based file row numbers (what the user sees in Excel) — parallel to rows.
  rowNumbers: number[];
};

// Columnar layout: cheap cross-row scans without allocating 12k row objects.
export type MappedSheet = {
  worksheet: string;
  rowNumbers: number[];
  // Canonical template field name → column of values. Only mapped fields are present.
  columns: Record<string, CellValue[]>;
};

export type ExternalKeySet = {
  values: CellValue[];
  numbers: CellValue[];
};

export const EXACTLY_ONE_OF_UNMAPPED = "exactlyOneOfUnmapped";
export const EVERY_ROW_LEFT_OUT = "everyRowLeftOut";
export const NO_ROWS_MATCHED = "noRowsMatched";
export const MISSING_KEY_COLUMN = "missingKeyColumn";
export const LOOKUP_RULE_TYPE: ValidationRule["type"] = "lookup";

export type IssueSeverity = "error";

export type ValidationIssue = {
  // Index into the run's flat issues array — stable handle for grid selection.
  issueID: number;
  worksheet: string;
  rowNumber: number;
  // Canonical field name; always set (a sheet rule picks the column its issue belongs on).
  field?: string;
  ruleType: string;
  severity: IssueSeverity;
  // From the message catalogue.
  message: string;
  // Offending cell value (capped length).
  value?: string;
};

export enum SkippedRowReason {
  Keyless = 1,
  Duplicate = 2,
  NoMatchingRow = 3,
  BlankJoinKey = 4,
  RepeatedJoinKey = 5,
}

type SkippedRowBase = {
  worksheet: string;
  // Original 1-based file row number — what the user sees in Excel.
  rowNumber: number;
  values: Record<string, string>;
  sheetName?: string;
};

export enum WorksheetOutcome {
  NoData = "noData",
  EveryRowLeftOut = "everyRowLeftOut",
  Validated = "validated",
}

export type SkippedRow =
  | (SkippedRowBase & {
      reason: SkippedRowReason.Keyless;
      // The key columns that were blank, in config order.
      blankFields: string[];
    })
  | (SkippedRowBase & {
      reason: SkippedRowReason.Duplicate;
      rowIdentifierFields: string[];
      duplicateOfRowNumber: number;
    })
  | JoinSkippedRow;

export function isJoinSkippedRow(row: SkippedRow): row is JoinSkippedRow {
  return (
    row.reason === SkippedRowReason.NoMatchingRow ||
    row.reason === SkippedRowReason.BlankJoinKey ||
    row.reason === SkippedRowReason.RepeatedJoinKey
  );
}

// Rows of a later sheet that could not be matched to a row of the worksheet's main sheet
export type JoinSkippedRow =
  | (SkippedRowBase & { reason: SkippedRowReason.NoMatchingRow; sheetName: string; mainSheetName: string })
  | (SkippedRowBase & { reason: SkippedRowReason.BlankJoinKey; sheetName: string; keyColumnName: string })
  | (SkippedRowBase & {
      reason: SkippedRowReason.RepeatedJoinKey;
      sheetName: string;
      keyColumnName: string;
      keyValue: string;
      duplicateOfRowNumber: number;
    });

export type KeylessRow = Extract<SkippedRow, { reason: SkippedRowReason.Keyless }>;
export type DuplicateRow = Extract<SkippedRow, { reason: SkippedRowReason.Duplicate }>;

export const isKeylessRow = (row: SkippedRow): row is KeylessRow => row.reason === SkippedRowReason.Keyless;
export const isDuplicateRow = (row: SkippedRow): row is DuplicateRow => row.reason === SkippedRowReason.Duplicate;

// Rows left out of a worksheet, by why they were left out
export type LeftOutRowCounts = {
  repeat: number;
  blankKey: number;
  noMatch: number;
};

export const NO_LEFT_OUT_ROWS: LeftOutRowCounts = { repeat: 0, blankKey: 0, noMatch: 0 };

const LEFT_OUT_COUNT_BY_REASON: Record<SkippedRowReason, keyof LeftOutRowCounts> = {
  [SkippedRowReason.Keyless]: "blankKey",
  [SkippedRowReason.BlankJoinKey]: "blankKey",
  [SkippedRowReason.Duplicate]: "repeat",
  [SkippedRowReason.RepeatedJoinKey]: "repeat",
  [SkippedRowReason.NoMatchingRow]: "noMatch",
};

export function countLeftOutRows(skippedRows: SkippedRow[]): LeftOutRowCounts {
  const counts = { ...NO_LEFT_OUT_ROWS };
  for (const row of skippedRows) counts[LEFT_OUT_COUNT_BY_REASON[row.reason]]++;
  return counts;
}

export function sumLeftOutCounts(counts: LeftOutRowCounts[]): LeftOutRowCounts {
  const total = { ...NO_LEFT_OUT_ROWS };
  for (const count of counts) {
    total.repeat += count.repeat;
    total.blankKey += count.blankKey;
    total.noMatch += count.noMatch;
  }
  return total;
}

// Imported main-sheet rows that a later sheet has no match for
export type MainRowsWithoutMatchCount = {
  mainSheetName: string;
  sheetName: string;
  rowCount: number;
};

export type WorksheetResultSummary = {
  worksheet: string;
  // Rows that were validated and will be submitted — skipped rows are not counted.
  totalRows: number;
  errorRows: number;
  errorCount: number;
  outcome: WorksheetOutcome;
  skippedRowCount: number;
  leftOutCounts: LeftOutRowCounts;
  mainRowsWithoutMatch: MainRowsWithoutMatchCount[];
};

export enum RelationshipOutcome {
  Checked = "checked",
  SourceNotAvailable = "sourceNotAvailable",
  NothingToCheck = "nothingToCheck",
}

export type RelationshipResult =
  | (WorksheetRelationship & { outcome: RelationshipOutcome.Checked; resolvedCount: number; unresolvedCount: number })
  | (WorksheetRelationship & { outcome: RelationshipOutcome.SourceNotAvailable | RelationshipOutcome.NothingToCheck });

export type ValidationRunSummary = {
  totalRows: number;
  validRows: number;
  errorRows: number;
  errorCount: number;
  skippedRowCount: number;
  leftOutCounts: LeftOutRowCounts;
  worksheets: WorksheetResultSummary[];
  relationships: RelationshipResult[];
  durationMs: number;
};
